import { useEffect, useState } from "react";
import { Plus, Trash2, AlertTriangle, CheckCircle2, Save } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { useApp } from "@/context/AppContext";
import { formatINR, CHART_COLORS } from "@/lib/format";
import { getRange, rangeParams } from "@/lib/period";
import PeriodFilter from "@/components/PeriodFilter";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";

export default function Allocation() {
  const { version, refresh } = useApp();
  const [cats, setCats] = useState([]);
  const [periodKey, setPeriodKey] = useState("this_month");
  const [custom, setCustom] = useState({ start: "", end: "" });
  const [range, setRange] = useState(getRange("this_month"));
  const [dash, setDash] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => { api.get("/allocation-categories").then((r) => setCats(r.data)); }, [version]);
  useEffect(() => { api.get("/dashboard", { params: rangeParams(range) }).then((r) => setDash(r.data)); }, [range, version]);

  const activeTotal = Math.round(cats.filter((c) => c.is_active).reduce((s, c) => s + Number(c.percentage || 0), 0) * 100) / 100;
  const valid = activeTotal === 100;
  const net = dash && dash.net_profit > 0 ? dash.net_profit : 0;

  const update = (i, k, v) => setCats((cs) => cs.map((c, idx) => (idx === i ? { ...c, [k]: v } : c)));
  const addCat = () => setCats((cs) => [...cs, { name: "New Category", percentage: 0, description: "", is_active: true, id: `tmp-${Date.now()}` }]);
  const removeCat = (i) => setCats((cs) => cs.filter((_, idx) => idx !== i));

  const save = async () => {
    setSaving(true);
    try {
      const payload = { categories: cats.map((c) => ({ name: c.name, percentage: Number(c.percentage), description: c.description || "", is_active: !!c.is_active })) };
      const { data } = await api.put("/allocation-categories", payload);
      setCats(data);
      toast.success("Allocation configuration saved");
      refresh();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Failed to save");
    } finally { setSaving(false); }
  };

  return (
    <div className="space-y-6" data-testid="allocation-page">
      <div className="flex flex-col gap-4 md:flex-row md:items-end md:justify-between">
        <div>
          <h1 className="font-heading text-3xl font-extrabold tracking-tight text-slate-900">Profit Allocation</h1>
          <p className="mt-1 text-slate-500">Divide net profit into purposes. Allocation does not reduce accounting profit.</p>
        </div>
        <PeriodFilter periodKey={periodKey} setPeriodKey={setPeriodKey} custom={custom} setCustom={setCustom} onChange={setRange} />
      </div>

      {/* Validation banner */}
      {valid ? (
        <div className="flex items-center gap-3 rounded-xl border border-emerald-200 bg-emerald-50 p-3" data-testid="alloc-valid">
          <CheckCircle2 className="h-5 w-5 text-emerald-500" />
          <span className="text-sm text-emerald-700">Allocation percentages total <b>100%</b>. Configuration is valid.</span>
        </div>
      ) : (
        <div className="flex items-center gap-3 rounded-xl border border-amber-200 bg-amber-50 p-3" data-testid="alloc-invalid">
          <AlertTriangle className="h-5 w-5 text-amber-500" />
          <span className="text-sm text-amber-700">
            {activeTotal < 100
              ? `Allocation percentages total ${activeTotal}%. Please allocate the remaining ${(100 - activeTotal).toFixed(2)}%.`
              : `Allocation percentages exceed 100% by ${(activeTotal - 100).toFixed(2)}%.`}
          </span>
        </div>
      )}

      <div className="grid gap-6 lg:grid-cols-2">
        {/* Config editor */}
        <div className="rounded-xl border border-slate-200 bg-white p-5 shadow-sm">
          <h3 className="mb-4 font-heading font-bold text-slate-900">Configure Percentages</h3>
          <div className="space-y-3">
            {cats.map((c, i) => (
              <div key={c.id || i} className="flex items-center gap-2" data-testid={`alloc-row-${i}`}>
                <span className="h-3 w-3 shrink-0 rounded-full" style={{ background: CHART_COLORS[i % CHART_COLORS.length] }} />
                <Input value={c.name} onChange={(e) => update(i, "name", e.target.value)} className="flex-1" />
                <div className="relative w-24">
                  <Input type="number" min="0" max="100" value={c.percentage} onChange={(e) => update(i, "percentage", e.target.value)} className="pr-6 text-right" data-testid={`alloc-pct-${i}`} />
                  <span className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-400">%</span>
                </div>
                <button onClick={() => removeCat(i)} className="rounded-lg p-2 text-slate-400 hover:bg-slate-100 hover:text-rose-600"><Trash2 className="h-4 w-4" /></button>
              </div>
            ))}
          </div>
          <div className="mt-4 flex items-center justify-between border-t border-slate-100 pt-4">
            <Button variant="outline" onClick={addCat} className="gap-2"><Plus className="h-4 w-4" /> Add Category</Button>
            <div className="text-sm">Total: <b className={valid ? "text-emerald-600" : "text-amber-600"}>{activeTotal}%</b></div>
          </div>
          <Button onClick={save} disabled={!valid || saving} className="mt-4 w-full gap-2 bg-orange-600 hover:bg-orange-700" data-testid="save-allocation">
            <Save className="h-4 w-4" /> {saving ? "Saving..." : "Save Configuration"}
          </Button>
        </div>

        {/* Live allocation table */}
        <div className="rounded-xl border border-slate-200 bg-white p-5 shadow-sm" data-testid="alloc-dashboard">
          <h3 className="mb-1 font-heading font-bold text-slate-900">Current Allocation</h3>
          <p className="mb-4 text-sm text-slate-500">Based on net profit of <b>{formatINR(net)}</b> for the selected period.</p>
          {dash?.is_loss && <div className="mb-3 rounded-lg bg-rose-50 p-2 text-sm text-rose-600">Net loss period — allocations are ₹0 until profitable.</div>}
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-xs uppercase tracking-wide text-slate-400">
                <th className="py-2">Purpose</th><th className="py-2 text-right">%</th><th className="py-2 text-right">Allocation</th>
              </tr>
            </thead>
            <tbody>
              {cats.filter((c) => c.is_active).map((c, i) => (
                <tr key={c.id || i} className="border-t border-slate-50">
                  <td className="py-2.5">
                    <span className="flex items-center gap-2">
                      <span className="h-2.5 w-2.5 rounded-full" style={{ background: CHART_COLORS[i % CHART_COLORS.length] }} />
                      <span className="text-slate-700">{c.name}</span>
                    </span>
                  </td>
                  <td className="py-2.5 text-right text-slate-600">{c.percentage}%</td>
                  <td className="py-2.5 text-right font-semibold text-slate-900">{formatINR(net * Number(c.percentage) / 100)}</td>
                </tr>
              ))}
              <tr className="border-t border-slate-200 font-bold">
                <td className="py-3">Total</td>
                <td className="py-3 text-right">{activeTotal}%</td>
                <td className="py-3 text-right text-orange-600">{formatINR(net * activeTotal / 100)}</td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
